import { useQueryClient } from '@tanstack/react-query';
import Dotdotdot from 'react-dotdotdot';
import Sticky from 'react-stickynode';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { DefaultLink, Link } from '@hivebrite/design-system/Link';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { separatorShadow, useBreakpoint } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventAttendeeVisibility from 'hb-react/shared/apps/Events/components/EventAttendeeVisibility';
import { RegistrationButton } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/RegistrationButton';
import EventShare from 'hb-react/shared/apps/Events/components/EventShare';
import RsvpDropdown from 'hb-react/shared/apps/Events/components/RsvpDropdown';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import AttachedFiles from 'hb-react/shared/components/DesignSystem/AttachedFiles';

import ParentTopic from './ParentTopic';

const RegistrationWrapper = ({ children }) => {
  const breakpoint = useBreakpoint();

  return breakpoint === 's' ? (
    <StickyButtonWrapper>
      <Sticky enabled top="header">
        {({ status }) => (
          <StickyButtonInner mb={2} $isSticked={status === Sticky.STATUS_FIXED}>
            {children}
          </StickyButtonInner>
        )}
      </Sticky>
    </StickyButtonWrapper>
  ) : (
    <Box mb={4}>{children}</Box>
  );
};

const ShareButtons = ({ event }) => {
  if (!event?.social_media_list?.length) {
    return null;
  }

  return (
    <ContentRow mb={0} pb={6}>
      <SectionLabel as="h2">{__('Share event')}</SectionLabel>
      <EventShare
        selected={event.social_media_list}
        url={`${window.location.origin}${Routes.networks_event_path(event.id)}`}
        title={event.title}
      />
    </ContentRow>
  );
};

const EventActions = (props) => {
  const { event, updateEvent } = useEventContext();
  const { currentUser } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  const { network_categories: networkCategories } = event;

  const rsvpDropdownOptions = [
    { value: 'cancel', label: I18n.t('js.event.status_decline') },
    { value: 'attend', label: I18n.t('js.event.status_going') },
  ];

  if (event.active_rsvp_maybe_attend_option) {
    rsvpDropdownOptions.push({
      value: 'maybe_attend',
      label: I18n.t('js.event.status_maybe'),
    });
  }

  return (
    <Box
      flexDirection="column"
      flexGrow={1}
      minWidth={0}
      {...props}
      data-testid="event-actions"
    >
      {event.participation_type === 'tickets' && (
        <ContentRow mb={4}>
          <RegistrationWrapper>
            <RegistrationButton />
          </RegistrationWrapper>
          {currentUser && event.user_total_tickets > 0 && (
            <Box font="body-asphalt" display="inline">
              {I18n.t('web.networks.events.tickets.list.already_bought', {
                number: event.user_total_tickets,
              })}{' '}
              <Link to={Routes.me_my_events_orders_path(event.id)}>
                {ns__(
                  'NetworkEvents|See my order',
                  'NetworkEvents|See my orders',
                  event.user_total_tickets,
                )}
              </Link>
            </Box>
          )}
        </ContentRow>
      )}
      {!event.cancelled &&
        event.participation_type === 'rsvp' &&
        event.rsvp_visible && (
          <ContentRow>
            <RegistrationWrapper>
              <RsvpDropdown
                key={event.id}
                defaultValue={event?.user_status}
                options={rsvpDropdownOptions}
                eventId={event.id}
                eventType={event.type}
                endDate={new Date(event.end_date)}
                onSave={(event) => {
                  queryClient.invalidateQueries({
                    queryKey: ['event', 'attendees', event.type, event.id],
                  });

                  updateEvent(event);
                }}
                reachedMaxCapacity={event.reached_max_rsvp_tickets}
              />
            </RegistrationWrapper>
          </ContentRow>
        )}
      {event.show_participants &&
        (event.response_type === 'attend' || event.user_total_tickets > 0) && (
          <ContentRow>
            <Box>
              <EventAttendeeVisibility />
            </Box>
          </ContentRow>
        )}
      {event.display_event_creator && event.parent_topic && (
        <ContentRow>
          <SectionLabel as="h2">{s__('Event|Created by')}</SectionLabel>
          <ParentTopic parentTopic={event.parent_topic} />
        </ContentRow>
      )}
      {(event.organizer ||
        event.contact_email ||
        event.contact_phone_number) && (
        <ContentRow>
          <SectionLabel as="h2">{__('About the organizer')}</SectionLabel>
          <OrganizerInfo
            organizer={event.organizer}
            contactEmail={event.contact_email}
            contactPhoneNumber={event.contact_phone_number}
          />
        </ContentRow>
      )}
      {!!networkCategories.length && (
        <ContentRow>
          <SectionLabel as="h2" mb={0}>
            {__('Categories')}
          </SectionLabel>
          <PillsList
            pills={networkCategories}
            seeMoreLabel={__('See More')}
            seeLessLabel={s__('Tags|See less')}
          />
        </ContentRow>
      )}
      <ShareButtons event={event} />
      {event.attachments?.length > 0 && (
        <ContentRow mt={0} alignItems="stretch">
          <AttachedFiles attachedFiles={event.attachments} />
        </ContentRow>
      )}
    </Box>
  );
};

export default EventActions;

// COMPONENTS
const OrganizerInfo = ({ organizer, contactEmail, contactPhoneNumber }) => (
  <Box flexDirection="column" font="body-semibold-asphalt">
    <Dotdotdot clamp={1} useNativeClamp>
      <span data-toggle="tooltip" title={organizer}>
        {organizer}
      </span>
    </Dotdotdot>
    {contactEmail && (
      <Box mb={1}>
        <ClampedLinkWorkaround clamp={1} useNativeClamp>
          <DefaultLink
            href={`mailto:${contactEmail}`}
            data-bypass-router
            data-toggle="tooltip"
            title={contactEmail}
          >
            {contactEmail}
          </DefaultLink>
        </ClampedLinkWorkaround>
      </Box>
    )}
    {contactPhoneNumber && (
      <Box>
        <Dotdotdot clamp={1} useNativeClamp>
          <DefaultLink
            href={`tel:${contactPhoneNumber}`}
            data-toggle="tooltip"
            title={contactPhoneNumber}
          >
            {contactPhoneNumber}
          </DefaultLink>
        </Dotdotdot>
      </Box>
    )}
  </Box>
);

// STYLED COMPONENTS
const ContentRow = styled(Box).attrs(({ mb }) => ({
  flexDirection: 'column',
  mb: mb ?? 6,
}))``;

const SectionLabel = styled(Box).attrs(({ mb }) => ({
  font: 'h4-semibold',
  mb: mb ?? 2,
}))``;

const StickyButtonWrapper = styled.div`
  position: relative;
  // because of EventDetailsContainer with z-index 2
  z-index: 3;
`;

const StickyButtonInner = styled.div`
  padding: ${({ theme }) => theme.space[4]}px 0;
  position: relative;

  ${({ $isSticked }) => $isSticked && ShadowBoxMixin};

  button {
    box-shadow: none;
  }
`;

const ShadowBoxMixin = css`
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    left: -${({ theme: { space, grid } }) => space[grid.colGap._]}px;
    right: -${({ theme: { space, grid } }) => space[grid.colGap._]}px;
  }

  &:before {
    top: 0;
    bottom: 0;
    background: ${({ theme }) => theme.themeVariables.frameBackgroundColor};
    z-index: 0;
  }

  &:after {
    z-index: -1;
    bottom: 0;
    height: 10px;

    ${separatorShadow}
  }
`;

/* This allows the box-shadow of the linkEffect to show up properly (it's missing 1px otherwise because of the overflow: hidden of
the Dotdotdot container) */
const ClampedLinkWorkaround = styled(Dotdotdot)`
  padding: 2px;
  margin: -2px;
`;
